import { useMemo, useState } from 'react';
import { Button, PageShell, SectionCard, Skeleton, toast } from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import {
  useExchangeRateMutation,
  useExchangeRateRefreshMutation,
  useExchangeRatesQuery,
} from '../hooks/useExchangeRatesQuery';
import { EXCHANGE_RATE_WRITE_ROLES } from '../lib/exchangeRateActionRbac';
import { ExchangeRateRow } from '../components/ExchangeRateRow';
import { ProviderConfigCard } from '../components/ProviderConfigCard';
import { ExchangeRateHistoryCard } from '../components/ExchangeRateHistoryCard';
import {
  EXCHANGE_RATE_CURRENCIES,
  type ExchangeRate,
  type ExchangeRateCurrency,
  type ExchangeRateUpdatePayload,
} from '../types';

export const ExchangeRatesPage = () => {
  const canWrite = useHasRole(EXCHANGE_RATE_WRITE_ROLES);
  const query = useExchangeRatesQuery();
  const { update } = useExchangeRateMutation();
  const refresh = useExchangeRateRefreshMutation();

  const [savingCurrency, setSavingCurrency] = useState<ExchangeRateCurrency | null>(null);

  const rateByCurrency = useMemo(() => {
    const map = new Map<ExchangeRateCurrency, ExchangeRate>();
    for (const rate of query.data ?? []) {
      map.set(rate.currencyCode, rate);
    }
    return map;
  }, [query.data]);

  const handleSave = (currency: ExchangeRateCurrency, payload: ExchangeRateUpdatePayload) => {
    setSavingCurrency(currency);
    update.mutate(
      { currencyCode: currency, payload },
      {
        onSuccess: () => toast.success(`${currency} 환율이 저장되었습니다.`),
        onError: () => toast.error(`${currency} 환율 저장에 실패했습니다.`),
        onSettled: () => setSavingCurrency(null),
      },
    );
  };

  const handleRefresh = () => {
    refresh.mutate(undefined, {
      onSuccess: (result) => {
        if (result.success) {
          toast.success(result.message ?? `환율 ${result.updatedCount}건을 갱신했습니다.`);
        } else {
          toast.error(result.message ?? '환율 즉시 갱신에 실패했습니다. (API 키/연동 상태 확인)');
        }
      },
      onError: () => toast.error('환율 즉시 갱신에 실패했습니다. (API 키/연동 상태 확인)'),
    });
  };

  return (
    <PageShell
      title="환율 설정"
      description="외화 결제 환산에 사용하는 환율을 관리합니다. 통화별로 자동(관세청 연동)/수동 모드를 선택하고, 자동 모드는 기준환율에 조정값을 가감해 최종 환율을 산출합니다. 상품 가격을 비워 두면 이 환율로 자동 계산됩니다."
      data-testid="exchange-rates-page"
      actions={
        canWrite ? (
          <Button
            variant="outline"
            size="sm"
            disabled={refresh.isPending}
            onClick={handleRefresh}
            data-testid="exchange-rate-refresh"
          >
            {refresh.isPending ? '갱신 중…' : '즉시 갱신'}
          </Button>
        ) : undefined
      }
    >
      <SectionCard title="통화별 환율">
        {query.isPending ? (
          <div role="status" aria-live="polite" className="space-y-2">
            {EXCHANGE_RATE_CURRENCIES.map((c) => (
              <Skeleton key={c} className="h-[88px] w-full" />
            ))}
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            aria-live="polite"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>환율 정보를 불러오지 못했습니다.</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              다시 시도
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {EXCHANGE_RATE_CURRENCIES.map((currency) => (
              <ExchangeRateRow
                key={currency}
                currency={currency}
                rate={rateByCurrency.get(currency)}
                canWrite={canWrite}
                saving={savingCurrency === currency}
                onSave={handleSave}
              />
            ))}
          </div>
        )}
      </SectionCard>

      <ProviderConfigCard canWrite={canWrite} />

      <ExchangeRateHistoryCard />
    </PageShell>
  );
};
